import Layout from '@src/components/Layout';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { Tab } from 'semantic-ui-react';
import { FarmersService } from '~/api/services/farmers/index';
import { RouteTypes } from '~/routes/constants';
import { FarmerProfileLogo, StyledDefaultProfile } from './style'
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useAuth } from '~/routes/components/AuthProvider';
import { MILK_MONEY_ACCOUNT_SLUG, PLANING_CONTRACT_SLUG } from '~/Constants';
import { FarmersQuery } from '~/QueryHooks/Farmers';
import { handleErrors } from '~/utils/helpers';

//Tabs
import ArticleBookings from './Tabs/ArticleBookings';
import BookingHistory from './Tabs/BookingHistory';
import Contacts from './Tabs/Contacts';
import Notes from './Tabs/Notes';
import MilkDeliveries from './Tabs/MilkDeliveries';
import SwissFarmerDetails from './Tabs/SwissFarmerDetails';
import TransponderId from './Tabs/TransponderId';
import MonthlyContracts from './Tabs/MonthlyContracts';
import { Nullable } from '~/types';
import { FarmerShowType } from '~/api/services/farmers/showFarmer';
import SwissMilkSamples from './Tabs/SwissMilkSamples';
import MilkTypes from './Tabs/MilkTypes';
import VatTable from './Tabs/VatTable';

const ShowFarmer: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { systemData: { modules, chEnv } } = useAuth()
  const milkMoneyModuleEnabled = modules.find(module => module.slug === MILK_MONEY_ACCOUNT_SLUG)
  const isSwissDairy = chEnv === 1

  const { id } = useParams()
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const evsNumber = searchParams.get('evsNumber') || ''
  const search = searchParams.get('search') || ''
  const agisId = searchParams.get('agisId') || ''

  const getCloseUrl = () => {
    const params = new URLSearchParams()
    if (evsNumber) params.set('evsNumber', evsNumber)
    if (search) params.set('search', search)
    if (agisId) params.set('agisId', agisId)
    const query = params.toString()
    return query ? `${RouteTypes.DAIRY_FACTORY_FARMERS}?${query}` : RouteTypes.DAIRY_FACTORY_FARMERS
  }

  const [isBusy, setIsBusy] = useState(false)
  const [showData, setShowData] = useState<Nullable<FarmerShowType>>(null)
  const [activeIndex, setActiveIndex] = useState<string | number>(0)

  const { data: farmerProfileSrc } = FarmersQuery.getFarmerProfileFile(id)
  const {
    data: farmerOptions,
    isLoading: isLoadingOptions,
    refetch: refetchOptions
  } = FarmersQuery.getFarmerOptions()

  const panes = [
    {
      menuItem: t('Operations Overview'),
      key: 'profile-details',
      to: '',
      render: () => <SwissFarmerDetails
        handleContractTypeChange={() => fetchShowData()}
        farmerOptions={farmerOptions}
      />
    },
    {
      menuItem: t('Contacts'),
      key: 'contacts',
      render: () => <Contacts />
    },
    {
      menuItem: t('Communication'),
      key: 'communication',
      render: () => <Notes />
    },
    {
      menuItem: t('Monthly contract quantity'),
      key: 'monthly-cotracts',
      render: () => <MonthlyContracts />,
      remove: showData?.farmer?.contractType?.slug !== PLANING_CONTRACT_SLUG
    },
    {
      menuItem: t('Milk types'),
      key: 'material-info',
      render: () => <MilkTypes />
    },
    {
      menuItem: t('Financial information'),
      key: 'financial-information',
      render: () => <VatTable
        createdAt={showData?.farmer?.createdAt}
        farmerData={showData?.farmer}
      />,
      remove: isSwissDairy
    },
    {
      menuItem: t('Milk samples'),
      key: 'milk-samples',
      render: () => <SwissMilkSamples />,
      remove: !milkMoneyModuleEnabled
    },
    {
      menuItem: t('Milk Deliveries'),
      key: 'milk-deliveries',
      render: () => <MilkDeliveries />,
      remove: !milkMoneyModuleEnabled
    },
    {
      menuItem: 'Transponder ID',
      key: 'transponder-ids',
      render: () => <TransponderId />,
      remove: !isSwissDairy
    },
    {
      menuItem: t('Article bookings'),
      key: 'article-bookings',
      render: () => <ArticleBookings
        farmer={`${showData?.farmer?.companyName} (${showData?.farmer?.supplierNo || ''})`}
        farmerData={showData?.farmer}
      />,
      remove: !milkMoneyModuleEnabled
    },
    {
      menuItem: t('Booking history'),
      key: 'bookings-history',
      render: () => <BookingHistory />,
      remove: !milkMoneyModuleEnabled
    }
  ]

  useEffect(() => {
    fetchShowData()
    refetchOptions()
  }, [t])

  useEffect(() => {
    if (tab) {
      const index = panes.filter(pane => !pane.remove)?.findIndex((option) => option.key === tab)
      setActiveIndex(index !== -1 ? index : 0)
    }
  }, [tab, panes])

  async function fetchShowData() {
    setIsBusy(true)
    try {
      const showDataResponse = await FarmersService.getShowFarmerData(id)
      setShowData(showDataResponse.data)
      if (tab) {
        const index = panes.filter(pane => !pane.remove)?.findIndex((option) => option.key === tab)
        setActiveIndex(index !== -1 ? index : 0)
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderPanes() {
    return <Tab
      style={{ margin: '30px 0px' }}
      menu={{ secondary: true, pointing: true }}
      onTabChange={(e, data) => {
        const panesFiltered = panes.filter(pane => !pane.remove)
        const params = Object.fromEntries(searchParams.entries());
        setSearchParams({ ...params, tab: panesFiltered[data.activeIndex].key }, { replace: true })
        setActiveIndex(data.activeIndex)
      }}
      activeIndex={activeIndex}
      panes={panes.filter(pane => !pane.remove)}
    />
  }

  function renderDefaultImage() {
    return <StyledDefaultProfile color='blue' name='user circle' size='big' />
  }

  function renderImage() {
    return <LazyLoadImage
      alt={''}
      effect='blur'
      src={farmerProfileSrc} />
  }

  function renderFarmerDetails() {
    const supplierNo = showData?.farmer?.supplierNo
    const evsNumber = showData?.farmer?.evsNumber || ''
    return !isBusy && <FarmerProfileLogo>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div className='farmer-logo-container'>
          {farmerProfileSrc ? renderImage() : renderDefaultImage()}
        </div>
        <div className='farmer-name-container'>
          {evsNumber && <div className='farmer-supplier'>
            {`EVS-Nr. ${evsNumber}`}
          </div>}
          {supplierNo && <div className='farmer-supplier'>
            {t('Supplier No. {{supplierNo}}', { supplierNo })}
          </div>}
          <div className='farmer-company-name'>{showData?.farmer?.companyName}</div>
        </div>
      </div>
    </FarmerProfileLogo>
  }

  return <Layout
    isBusy={isBusy || isLoadingOptions}
    segmented
    title={t('Farmer profile')}
    close={getCloseUrl()}
  >
    {renderFarmerDetails()}
    {renderPanes()}
  </Layout>;
};

export default ShowFarmer;
